/**
 * TagadaPay External Page Tracker
 *
 * Standalone script for tracking users on external pages that are part of a
 * TagadaPay funnel but not hosted on the TagadaPay platform.
 *
 * Use cases:
 *   - WooCommerce / WordPress storefronts
 *   - Custom landing pages
 *   - Any external page that is a funnel step
 *
 * @example CDN usage
 * ```html
 * <script src="https://cdn.jsdelivr.net/npm/@tagadapay/plugin-sdk/dist/external-tracker.min.js"></script>
 * <script>
 *   TagadaTracker.init({
 *     storeId: 'store_xxx',
 *     accountId: 'acc_xxx',
 *     stepId: 'step_xxx',
 *   });
 * </script>
 * ```
 */

import { createTagadaClient } from './index';
import type { TagadaClient } from '../core/client';
import type { SimpleFunnelContext, FunnelActionType } from '../core/resources/funnel';
import { setClientToken } from '../core/utils/tokenStorage';

// ============================================================================
// VERSION
// ============================================================================

export const TRACKER_VERSION = '1.0.0';

// ============================================================================
// TYPES
// ============================================================================

export interface TagadaTrackerConfig {
    /** Store ID (required) */
    storeId: string;
    /** Account ID (required — session init will fail without it) */
    accountId: string;
    /** Funnel ID (optional — can be extracted from URL param) */
    funnelId?: string;
    /** Step ID (required for external pages — URL-to-step mapping only works for TagadaPay-hosted pages) */
    stepId: string;
    /** Step name (optional — for analytics/debugging) */
    stepName?: string;
    /** Step type (optional — e.g., 'landing', 'offer', 'external') */
    stepType?: string;
    /** API base URL (defaults to https://api.tagada.io) */
    apiBaseUrl?: string;
    /** Enable debug logging */
    debug?: boolean;
    /** Callback when session is ready */
    onReady?: (session: ExternalTrackerSession) => void;
    /** Callback on error — when provided, init() will NOT throw */
    onError?: (error: Error) => void;
}

export interface ExternalTrackerSession {
    sessionId: string;
    customerId: string;
    storeId: string;
    funnelId?: string;
    currentStepId?: string;
    cmsToken?: string;
}

export interface NavigateOptions {
    /** Event type for navigation (e.g., 'offer.accepted', 'form.submitted') */
    eventType: string;
    /** Event data */
    eventData?: Record<string, unknown>;
    /** Override return URL */
    returnUrl?: string;
    /** Whether to auto-redirect after navigation (default: true) */
    autoRedirect?: boolean;
}

export interface TrackEventOptions {
    /** Event name (e.g., 'button_click', 'scroll_depth', 'video_play') */
    name: string;
    /** Arbitrary event data */
    data?: Record<string, unknown>;
}

// ============================================================================
// UTILITIES
// ============================================================================

function getUrlParam(name: string): string | null {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
}

function log(debug: boolean, ...args: unknown[]): void {
    if (debug) {
        console.log('[TagadaTracker]', ...args);
    }
}

function warn(...args: unknown[]): void {
    console.warn('[TagadaTracker]', ...args);
}

function validate(config: TagadaTrackerConfig): void {
    if (!config.storeId || typeof config.storeId !== 'string') {
        throw new Error('TagadaTracker: storeId is required and must be a non-empty string.');
    }
    if (!config.accountId || typeof config.accountId !== 'string') {
        throw new Error('TagadaTracker: accountId is required and must be a non-empty string.');
    }
    if (!config.stepId || typeof config.stepId !== 'string') {
        throw new Error('TagadaTracker: stepId is required and must be a non-empty string.');
    }
    if (config.apiBaseUrl) {
        try {
            new URL(config.apiBaseUrl);
        } catch {
            throw new Error(`TagadaTracker: apiBaseUrl is not a valid URL: "${config.apiBaseUrl}"`);
        }
    }
}

async function retryWithBackoff<T>(
    fn: () => Promise<T>,
    maxRetries: number,
    debug: boolean,
): Promise<T> {
    let lastError: Error | undefined;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (err) {
            lastError = err instanceof Error ? err : new Error(String(err));
            if (attempt < maxRetries) {
                const delay = Math.min(1000 * 2 ** attempt, 8000);
                log(debug, `Retry ${attempt + 1}/${maxRetries} in ${delay}ms...`);
                await new Promise((r) => setTimeout(r, delay));
            }
        }
    }
    throw lastError;
}

// ============================================================================
// MAIN CLASS
// ============================================================================

class TagadaExternalTracker {
    private config: TagadaTrackerConfig | null = null;
    private client: TagadaClient | null = null;
    private initialized = false;
    private initializing = false;
    private unsubscribe: (() => void) | null = null;

    /** Tracker version */
    readonly version = TRACKER_VERSION;

    /**
     * Initialize the tracker.
     * When `onError` is provided, errors are passed to the callback and init()
     * resolves to `null` instead of throwing.
     */
    async init(config: TagadaTrackerConfig): Promise<ExternalTrackerSession | null> {
        if (this.initialized) {
            log(config.debug || false, 'Already initialized — returning existing session.');
            return this.getSession();
        }

        if (this.initializing) {
            log(config.debug || false, 'Initialization in progress — skipping duplicate call.');
            return null;
        }

        this.initializing = true;
        this.config = { debug: false, ...config };
        const debug = this.config.debug!;

        try {
            validate(this.config);
        } catch (err) {
            this.initializing = false;
            const error = err instanceof Error ? err : new Error(String(err));
            if (this.config.onError) {
                this.config.onError(error);
                return null;
            }
            throw error;
        }

        log(debug, `🚀 Initializing external tracker v${TRACKER_VERSION}`, {
            storeId: config.storeId,
            accountId: config.accountId,
            stepId: config.stepId,
            funnelId: config.funnelId,
        });

        try {
            const urlToken = getUrlParam('token');
            if (urlToken) {
                setClientToken(urlToken);
                log(debug, '🔑 Bootstrapped token from URL');
            }

            this.client = createTagadaClient({
                debugMode: debug,
                features: { funnel: { skipAutoInit: true } },
                rawPluginConfig: {
                    storeId: this.config.storeId,
                    accountId: this.config.accountId,
                },
                customApiConfig: this.config.apiBaseUrl ? {
                    apiConfig: {
                        baseUrl: this.config.apiBaseUrl.trim(),
                    },
                } : undefined,
            });

            await this.waitForClientReady();

            const funnelContext = await retryWithBackoff(
                () => this.initializeFunnel(),
                2,
                debug,
            );

            log(debug, '✅ Session initialized:', funnelContext);

            this.initialized = true;
            const session = this.getSession();
            if (session) {
                this.config.onReady?.(session);
            }
            return session;
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            log(debug, '❌ Initialization failed:', error);

            if (this.config.onError) {
                this.config.onError(error);
                return null;
            }
            throw error;
        } finally {
            this.initializing = false;
        }
    }

    /** Get current session data. */
    getSession(): ExternalTrackerSession | null {
        if (!this.client?.funnel?.state.context) return null;
        const ctx = this.client.funnel.state.context;
        return {
            sessionId: ctx.sessionId,
            customerId: ctx.customerId,
            storeId: ctx.storeId,
            funnelId: ctx.funnelId,
            currentStepId: ctx.currentStepId,
            cmsToken: this.client.state.token || undefined,
        };
    }

    /** Whether the tracker is fully initialized and ready. */
    isReady(): boolean {
        return this.initialized && !!this.client?.funnel?.state.context;
    }

    /**
     * Navigate to the next step in the funnel.
     * By default, auto-redirects the browser window.
     */
    async navigate(options: NavigateOptions): Promise<{ url: string } | null> {
        if (!this.isReady()) {
            throw new Error('TagadaTracker: not initialized. Call init() first.');
        }

        log(this.config!.debug!, '🚀 Navigating:', options);

        const shouldAutoRedirect = options.autoRedirect !== false;

        try {
            const result = await this.client!.funnel!.navigate(
                {
                    type: options.eventType as FunnelActionType,
                    data: options.eventData || {},
                },
                { autoRedirect: false },
            );

            if (result?.url) {
                log(this.config!.debug!, '✅ Navigation result:', result.url);
                if (shouldAutoRedirect && typeof window !== 'undefined') {
                    window.location.href = options.returnUrl || result.url;
                }
                return { url: result.url };
            }
            return null;
        } catch (error) {
            log(this.config!.debug!, '❌ Navigation failed:', error);
            throw error;
        }
    }

    /**
     * Track a custom event (e.g., button click, scroll depth, video play).
     * Events are sent through the funnel orchestrator for unified analytics.
     */
    async trackEvent(options: TrackEventOptions): Promise<void> {
        if (!this.isReady()) {
            warn('trackEvent called before init — event dropped:', options.name);
            return;
        }

        log(this.config!.debug!, '📊 Tracking event:', options.name, options.data);

        try {
            await this.client!.funnel!.navigate(
                {
                    type: options.name as FunnelActionType,
                    data: options.data || {},
                },
                { autoRedirect: false },
            );
        } catch {
            // Non-critical — swallow errors for tracking events
            log(this.config!.debug!, '⚠️ Event tracking failed (non-critical):', options.name);
        }
    }

    /** Get customer ID. */
    getCustomerId(): string | null {
        return this.client?.state.auth.customer?.id
            || this.client?.funnel?.state.context?.customerId
            || null;
    }

    /** Get funnel session ID. */
    getFunnelSessionId(): string | null {
        return this.client?.funnel?.state.context?.sessionId || null;
    }

    /**
     * Build a URL that preserves funnel session context.
     * Use this to link to other external pages within the same funnel.
     */
    buildUrl(baseUrl: string, additionalParams?: Record<string, string>): string {
        const session = this.getSession();
        if (!session) return baseUrl;

        const url = new URL(baseUrl, typeof window !== 'undefined' ? window.location.origin : undefined);
        url.searchParams.set('funnelSessionId', session.sessionId);
        if (session.cmsToken) url.searchParams.set('token', session.cmsToken);
        if (session.funnelId) url.searchParams.set('funnelId', session.funnelId);
        url.searchParams.set('storeId', session.storeId);

        if (additionalParams) {
            Object.entries(additionalParams).forEach(([key, value]) => {
                url.searchParams.set(key, value);
            });
        }
        return url.toString();
    }

    /** Get the underlying SDK client (advanced usage). */
    getClient(): TagadaClient | null {
        return this.client;
    }

    /**
     * Reset the tracker to a new step without re-creating the session.
     * Useful for SPAs that navigate between funnel steps client-side.
     */
    async reset(newStepId: string): Promise<ExternalTrackerSession | null> {
        if (!this.client || !this.config) {
            throw new Error('TagadaTracker: not initialized. Call init() first.');
        }
        log(this.config.debug!, '🔄 Resetting to step:', newStepId);
        this.config.stepId = newStepId;
        const ctx = await this.initializeFunnel();
        const session = this.getSession();
        if (session) this.config.onReady?.(session);
        return session;
    }

    /**
     * Destroy the tracker, clean up event listeners and state.
     * Call this when the tracker is no longer needed (e.g., SPA teardown).
     */
    destroy(): void {
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
        if (this.client) {
            this.client.destroy?.();
        }
        this.client = null;
        this.config = null;
        this.initialized = false;
        this.initializing = false;
        log(false, '🗑️ Tracker destroyed');
    }

    // ========================================================================
    // PRIVATE METHODS
    // ========================================================================

    private async waitForClientReady(): Promise<void> {
        if (!this.client) return;

        // Wait for isInitialized which is set AFTER the full init sequence:
        // anonymous token → session/v2/init → isInitialized=true
        // Do NOT resolve on token alone — that fires before session init.
        return new Promise<void>((resolve) => {
            if (this.client?.state.isInitialized) {
                resolve();
                return;
            }

            let resolved = false;
            const timeout = setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    log(this.config?.debug || false, '⏱️ Client ready timeout — proceeding');
                    resolve();
                }
            }, 10000);

            this.unsubscribe = this.client!.subscribe(() => {
                if (!resolved && this.client?.state.isInitialized) {
                    resolved = true;
                    clearTimeout(timeout);
                    resolve();
                }
            });
        });
    }

    private async initializeFunnel(): Promise<SimpleFunnelContext | null> {
        if (!this.client?.funnel) return null;

        const customerId = this.client.state.auth.customer?.id;
        const sessionId = this.client.state.auth.session?.sessionId;

        if (!customerId && !sessionId) {
            warn('No auth session available — funnel init may fail.');
        }

        const authSession = {
            customerId: customerId || 'anon_placeholder',
            sessionId: sessionId || 'sess_placeholder',
        };

        const store = {
            id: this.config!.storeId,
            accountId: this.config!.accountId,
        };

        const entryStepId = this.config!.stepId;
        log(this.config!.debug!, '🔍 Initializing funnel at step:', entryStepId);

        return this.client.funnel.initialize(
            authSession,
            store,
            this.config!.funnelId || getUrlParam('funnelId') || undefined,
            entryStepId,
        );
    }
}

// ============================================================================
// GLOBAL INSTANCE
// ============================================================================

const TagadaTracker = new TagadaExternalTracker();

export { TagadaTracker, TagadaExternalTracker };

if (typeof window !== 'undefined') {
    (window as any).TagadaTracker = TagadaTracker;
}
